import { useState, useEffect } from 'react';
import { useDashboard } from '../../hooks/useDashboard';
import { useCache } from '../../hooks/useCache';
import { fetchFootfallDirectoryHours, fetchFootfallDirectory, BACKEND_URL } from '../../api';
import ImageLightbox from '../ui/ImageLightbox';
import { Skeleton } from '../ui/Skeleton';
import styles from './FootfallDirectory.module.css';

function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('/api')) return `${BACKEND_URL}${url}`;
  return url;
}

function fmtDateShort(d) {
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDayMonth(d) {
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
}

function FolderIcon() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z"
            stroke="#00A3FF" strokeWidth="1.5" strokeLinejoin="round" fill="#e6f6ff"/>
    </svg>
  );
}

function seededRand(seed, i) {
  const x = Math.sin(seed * 127 + i * 31) * 43758;
  return x - Math.floor(x);
}

function CctvPersonPlaceholder({ color = '#00A3FF', seed = 65 }) {
  const figs = [
    [50 + (seed % 20), 65],
    [105 + ((seed * 3) % 25), 62],
    [160 + ((seed * 7) % 20), 67],
  ];
  return (
    <svg viewBox="0 0 210 118" style={{ width: '100%', height: '100%', background: '#0f1621', display: 'block' }}>
      {Array.from({ length: 20 }, (_, i) => (
        <line key={i} x1="0" y1={i * 6} x2="210" y2={i * 6} stroke="rgba(255,255,255,0.015)" strokeWidth="1"/>
      ))}
      <line x1="0" y1="78" x2="210" y2="78" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"/>
      {figs.map(([fx, fy], i) => (
        <g key={i}>
          <ellipse cx={fx} cy={fy + 2} rx="5" ry="7" fill={color} opacity="0.5"/>
          <circle cx={fx} cy={fy - 9} r="3" fill={color} opacity="0.5"/>
          <rect x={fx - 8} y={fy - 14} width="16" height="24" fill="none" stroke={color} strokeWidth="0.7" opacity="0.35" rx="1"/>
        </g>
      ))}
      <path d="M5 14L5 6L13 6"   fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1.5"/>
      <path d="M205 14L205 6L197 6" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1.5"/>
      <path d="M5 104L5 112L13 112"  fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1.5"/>
      <path d="M205 104L205 112L197 112" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1.5"/>
    </svg>
  );
}

function PersonCard({ person, color, onImageClick }) {
  const entryImg = resolveImageUrl(person.entryTimeImage);
  const hasImage = !!entryImg;
  const seed = (person.tempId || 1) * 7;

  return (
    <div className={styles.camCard} onClick={() => hasImage && onImageClick()} style={{ cursor: hasImage ? 'zoom-in' : 'default' }}>
      <div className={styles.camFrame}>
        {hasImage ? (
          <img
            src={entryImg}
            alt={`Person ${person.tempId}`}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <CctvPersonPlaceholder color={color} seed={seed} />
        )}
      </div>
      <div className={styles.camInfo}>
        <div className={styles.camRow}>
          <div className={styles.camTime}>{person.entryTime || '—'}</div>
          <div className={styles.camBadge}>#{person.tempId}</div>
        </div>
      </div>
    </div>
  );
}

export default function FootfallDirectory() {
  const { currentStore, startDate, endDate, datesParam } = useDashboard();
  const allDates = datesParam ? datesParam.split(',') : [startDate];
  const isMultiDate = allDates.length > 1;

  const [selectedDate, setSelectedDate] = useState(isMultiDate ? null : startDate);
  const [activeHour, setActiveHour]     = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    setSelectedDate(allDates.length > 1 ? null : startDate);
  }, [startDate, endDate, datesParam]);

  // Cache hourly counts
  const hoursKey = currentStore ? `dir-hours:${currentStore.storeId}:${selectedDate}` : null;
  const { data: hoursData, loading } = useCache(hoursKey, () => fetchFootfallDirectoryHours(currentStore.storeId, selectedDate));
  const hours = hoursData?.hours || [];

  // Auto-select first available hour with captures when hours data arrives
  useEffect(() => {
    if (!hours.length) return;
    const first = hours.find(h => h.isAvailable && h.count > 0);
    if (first) setActiveHour(first.folder);
  }, [hoursKey, hours.length]);

  // Cache people for selected hour
  const peopleKey = currentStore && activeHour ? `dir-people:${currentStore.storeId}:${selectedDate}:${activeHour}` : null;
  const { data: peopleData, loading: loadingPeople } = useCache(peopleKey, () => fetchFootfallDirectory(currentStore.storeId, selectedDate, activeHour));
  const people = peopleData?.footfallData || [];

  if (!currentStore) return null;

  const totalCount = hours.reduce((a, h) => a + h.count, 0);
  const imageUrls = people.map(p => resolveImageUrl(p.entryTimeImage)).filter(Boolean);

  return (
    <div className="section">
      <div className="sec-hdr">
        <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--g900)' }}>Footfall Image Directory</h2>
      </div>

      {/* Folder grid (multi-date) — shown until user clicks into a date */}
      {isMultiDate && !selectedDate && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 16, padding: '8px 0' }}>
          {allDates.map(d => (
            <button
              key={d}
              onClick={() => { setSelectedDate(d); setActiveHour(null); }}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                padding: '14px 8px', background: 'transparent', border: 'none',
                borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--g50)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <FolderIcon />
              <span style={{ fontSize: 12, color: 'var(--g700)', fontWeight: 500 }}>{fmtDayMonth(d)}</span>
            </button>
          ))}
        </div>
      )}

      {/* Date drill-in header (multi-date only) */}
      {isMultiDate && selectedDate && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <button
            onClick={() => { setSelectedDate(null); setActiveHour(null); }}
            style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--g200)', background: 'var(--white)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            aria-label="Back to folders"
          >
            <svg width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M8 2L4 6l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--g900)' }}>{fmtDateShort(selectedDate)}</span>
        </div>
      )}

      {!isMultiDate && (
        <div style={{ fontSize: 11, color: 'var(--g400)', marginBottom: 8 }}>{fmtDateShort(selectedDate)}</div>
      )}

      {/* Hour tabs (only when a date is drilled into) */}
      {selectedDate && (loading ? (
        <div style={{ display: 'flex', gap: 8, padding: '8px 0' }}>
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} width={64} height={32} radius={8} />)}
        </div>
      ) : (
        <div className={styles.tabs}>
          {hours.map(h => (
            <button
              key={h.folder}
              className={`${styles.tab} ${activeHour === h.folder ? styles.activeTab : ''}`}
              onClick={() => { if (h.isAvailable) { setActiveHour(h.folder); } }}
              style={{ opacity: h.isAvailable ? 1 : 0.4 }}
            >
              {h.folder}<span className={styles.tabCount}>{h.count}</span>
            </button>
          ))}
        </div>
      ))}

      {/* People grid (scrollable) */}
      {selectedDate && (loadingPeople ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, padding: '8px 0' }}>
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={180} radius={10} />)}
        </div>
      ) : people.length === 0 ? (
        <div className={styles.empty}>No captures in this time window</div>
      ) : (
        <div style={{ maxHeight: 520, overflowY: 'auto', paddingRight: 4 }}>
          <div className={styles.grid}>
            {people.map((p, i) => (
              <PersonCard key={p.tempId || i} person={p} color={currentStore.color} onImageClick={() => setLightboxIndex(i)} />
            ))}
          </div>
        </div>
      ))}
      {lightboxIndex !== null && <ImageLightbox images={imageUrls} currentIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />}
    </div>
  );
}
